import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Typography,
} from "@mui/material";
import {
  getMsalLoginErrorMessage,
  loginWithExternalMicrosoft,
  loginWithInternalMicrosoft,
} from "../authConfig.js";
import { trackEvent } from "../analytics/ga4.js";
import { resolvePortalHost } from "../portal/portalUtils.js";

const Login = () => {
  const [errorMessage, setErrorMessage] = useState("");
  const [pendingLogin, setPendingLogin] = useState(null);
  const portal = resolvePortalHost().portal;
  const loginType = portal === "grower" ? "external" : "internal";
  const isGrowerPortal = portal === "grower";

  const handleLogin = async (loginType) => {
    setErrorMessage("");
    setPendingLogin(loginType);
    trackEvent("login_started", {
      workflow_area: "auth",
      login_type: loginType,
    });

    try {
      if (loginType === "internal") {
        await loginWithInternalMicrosoft();
        // Internal sign-in happens on a separate MSAL instance from the one msal-react's
        // MsalProvider/useMsal() context watches (external, by design -- see authConfig.js). A full
        // reload forces a fresh app boot that re-resolves the active account from the shared account
        // cache, rather than relying on the external instance's context to reactively notice a login
        // that happened on a different instance.
        window.location.assign("/");
        return;
      }

      await loginWithExternalMicrosoft();
    } catch (error) {
      console.error("MSAL login failed", error);
      setErrorMessage(getMsalLoginErrorMessage(error));
    } finally {
      setPendingLogin(null);
    }
  };

  return (
    <Container maxWidth="sm">
      <Card className="shadow border border-gray-300">
        <CardContent className="p-6">
          <Box className="flex flex-col gap-4">
            <Typography variant="h4" component="h1">
              Sign in
            </Typography>
            <Typography variant="body1">
              {isGrowerPortal
                ? "Sign in with your grower account."
                : "Sign in with your organization account."}
            </Typography>
            {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
            <Box className="flex flex-col gap-3">
              <Button
                variant="contained"
                color="warning"
                fullWidth
                disabled={pendingLogin !== null}
                onClick={() => handleLogin(loginType)}
              >
                {pendingLogin
                  ? "Starting Microsoft Sign-In..."
                  : isGrowerPortal
                    ? "Grower Sign-In"
                    : "Organization Sign-In"}
              </Button>
              <Typography variant="body2" color="text.secondary">
                {isGrowerPortal
                  ? "Self-service access for growers."
                  : "Access is provisioned by a Field to Market administrator."}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Login;
